<template>
    <div class="container-fluid px-4">

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" searchable :selectable="false"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">

            <template #actions="{ row }">
                <button class="btn btn-sm my-0 py-0" @click="editInstances(row.ai_workgroup_id)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button class="btn btn-sm my-0 py-0" @click="openLogsModal(row)">
                    <i class="material-icons text-lg text-info">chat</i>
                </button>
            </template>
        </BaseTable>

        <ContentModal v-model="showLogs" title="Logs" size="xl">
            <BaseTable :columns="logColumns" :rows="auditLogs" :searchable="false" :pagination="false" class="mt-3" />
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute, useRouter } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import { useBreadcrumb } from '@/composables/useBreadcrumb'

const toast = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()

const searchTerm = ref('')
const selectedWorkgroup = ref(null)
const showLogs = ref(false)
const auditLogs = ref([]);

const workgroupId = Number(route.params.workgroupid)
const locationId = Number(route.params.locationid)

const workgroups = computed(() => store.getters.workgroupInstances)
const pagination = computed(() => store.getters.workgroupInstancesPagination)
const loading = computed(() => store.getters.workgroupInstancesLoading)

const columns = [
    { key: 'start_datetime_formatted', label: 'Instance Date' },
    { key: 'name', label: 'Instance Workgroup Name' },
    { key: 'description', label: 'Description' },
    { key: 'persons_needed', label: 'Person Count' },
    { key: 'persons_assigned', label: 'Persons Scheduled' },
    { key: 'skillname', label: 'Skill' },
    { key: 'teamname', label: 'Team' },
]

const logColumns = [
    { key: "messagedt", label: "Date/Time" },
    { key: "message", label: "Message" },
];

const fetchInstances = async (page = 1, pageSize = 10) => {
    // loading.value = true // Managed by store getter
    try {
        await store.dispatch('fetchWorkgroupInstances', {
            workgroupId,
            includePast: false,
            limit: pageSize,
            page,
        })
        // workgroupsData.value = store.state.workgroups || [] // Removed
        // paginationData.value = store.state.pagination || {} // Removed
    } catch (error) {
        console.error('Error fetching workgroup instances:', error)
        toast.error('Failed to load workgroup instances', { position: 'top-right' })
    } finally {
        // loading.value = false // Managed by store getter
    }
}

const onChangePage = (page) => {
    if (!page) return
    fetchInstances(page, pagination.value.pageSize) // Pass current pageSize
}

const onUpdatePageSize = (size) => {
    fetchInstances(1, size)
}

const onSearchChanged = (term) => {
    searchTerm.value = term
    fetchInstances(1, pagination.value.pageSize) // Pass current pageSize
}

const openLogsModal = async (row) => {
    selectedWorkgroup.value = row
    showLogs.value = true
    try {
        const logs = await store.dispatch('fetchWorkgroupInstanceAuditLogs', row.ai_workgroup_id);
        auditLogs.value = logs;
    } catch (error) {
        console.error('Error fetching audit logs:', error);
        toast.error('Failed to load audit logs', { position: 'top-right' });
    }
}

const { pushCrumb } = useBreadcrumb()

const editInstances = (id) => {
    const row = workgroups.value.find(r => r.ai_workgroup_id === id)
    pushCrumb({
        label: row?.name || `Instance Workgroup #${id}`,
        icon: 'groups',
        route: { name: 'editWorkgroupInstance', params: { locationid: locationId, instanceworkgroupid: id } },
    })
    router.push({
        name: 'editWorkgroupInstance',
        params: { locationid: locationId, instanceworkgroupid: id },
    })
}

onMounted(() => {
    // loading.value = true; // Managed by store getter
    fetchInstances()
})
</script>